import { toast } from 'sonner';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useTaskTagDisplay, useUpdateTaskTagDisplay } from '../../hooks/useTaskTagDisplay';
import type { TaskTagDisplay } from '../../lib/tasks/taskTagDisplay';
import { cn } from '../../lib/cn';

const OPTIONS: { value: TaskTagDisplay; label: string; hint: string }[] = [
  { value: 'chips', label: 'Pastilles', hint: 'Le nom des étiquettes après le titre de la tâche.' },
  { value: 'border', label: 'Bordure', hint: 'Une bande de couleur sur le bord gauche. Son nom apparaît au survol.' },
];

/** Section « Affichage des tâches » de Mon compte : comment chacun voit les étiquettes. */
export function ProfileTaskDisplaySection() {
  const { tags } = useEffectiveTaskFields();
  const mode = useTaskTagDisplay();
  const update = useUpdateTaskTagDisplay();

  // Étiquettes désactivées pour le cabinet : aucun choix à faire.
  if (!tags) return null;

  function pick(next: TaskTagDisplay) {
    if (next === mode) return;
    update.mutate(next, {
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer'),
    });
  }

  return (
    <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Étiquettes des tâches</h2>
        <p className="mt-1 text-sm text-cime-text-muted">
          Comment les étiquettes apparaissent dans vos listes de tâches. Le choix ne vaut que pour vous.
        </p>
      </div>
      <div role="radiogroup" aria-label="Affichage des étiquettes" className="grid gap-3 sm:grid-cols-2">
        {OPTIONS.map((option) => {
          const on = option.value === mode;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={update.isPending}
              onClick={() => pick(option.value)}
              className={cn(
                'cursor-pointer rounded-lg border bg-white p-3 text-left transition-colors',
                on ? 'border-cime-primary ring-1 ring-cime-primary' : 'border-neutral-200 hover:border-neutral-300',
              )}
            >
              <ExampleRow mode={option.value} />
              <span className="mt-2.5 block text-sm font-semibold text-cime-text-primary">{option.label}</span>
              <span className="mt-0.5 block text-xs text-cime-text-muted">{option.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Petit exemple de ligne de tâche dans chacun des deux modes. */
function ExampleRow({ mode }: { mode: TaskTagDisplay }) {
  return (
    <span className="relative flex h-9 items-center gap-2 overflow-hidden rounded-md border border-neutral-200 bg-white pl-3 pr-2">
      {mode === 'border' ? <span className="absolute inset-y-0 left-0 w-1 bg-[#16a34a]" aria-hidden /> : null}
      <span className="h-3.5 w-3.5 flex-shrink-0 rounded-full border border-neutral-300" aria-hidden />
      <span className="truncate text-[13px] text-cime-text-primary">Relancer le greffe</span>
      {mode === 'chips' ? (
        <span className="ml-auto shrink-0 rounded-full bg-[#16a34a] px-1.5 text-[10px] leading-4 text-white">Facturer</span>
      ) : null}
    </span>
  );
}
